import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { finalize, Subject, take } from 'rxjs';
import { AliasService } from '../../services/alias.service';
import { NotificationService } from '../../services/notification.service';
import { LoadingComponent } from "../../shared/loading/loading.component";
import { ModalComponent } from '../../shared/modal/modal.component';
import { SearchBarComponent } from '../../shared/searchbar/searchbar.component';
import { SharedTableComponent } from '../../shared/shared-table/shared-table.component';

@Component({
  selector: 'app-alias',
  standalone: true,
  templateUrl: './alias.component.html',
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    SharedTableComponent,
    SearchBarComponent,
    MatSnackBarModule,
    LoadingComponent
]
})
export class AliasComponent implements OnInit, OnDestroy {

  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly aliasService: AliasService,
    private readonly notificationService: NotificationService
  ) {}

  isLoading = false; //loading in table
  hasError = false;
  showFullScreenLoading = false; //loading full screen

  aliasList$ = new Subject<void>();


  // Stato query
  sortBy = 'name:asc';
  sortField = '';
  sortDirection = '';
  searchQuery: string = '';

  // Paginazione server-side
  pageIndex = 0;
  pageSize = 10;
  totalItems = 0;

  // DataSource tabella
  dataSourceAlias = new MatTableDataSource<any>();

  // campi nascosti per la modale
  hiddenFieldsValues: Record<string, any> = {
    type: 'SIMPLE'
  };

  ngOnInit(): void {
    this.loadAliasList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
  }

  loadAliasList(pageIndex: number, pageSize: number, sortBy: string, aliasFilter: string) {
    this.isLoading = true;
    this.hasError = false;
    this.aliasService.getAliasList(pageIndex, pageSize, sortBy, aliasFilter).pipe(finalize(() => { 
      this.isLoading = false;
      this.aliasList$.next();
     })).subscribe({
      next: (response) => {
        const content = response?.content ?? [];
        this.dataSourceAlias.data = content;
        this.totalItems = response?.totalElements ?? 0;
        this.pageIndex = (response?.number ?? pageIndex) ?? 0;
        this.pageSize  = (response?.size ?? pageSize) ?? this.pageSize;
      },
      error: (err) => {
        console.log('errore nel recupero dei dati', err);
        this.dataSourceAlias.data = [];
        this.totalItems = 0;
        this.hasError = true;
      }
    });
  }

  goToDetail(row: any) {
    localStorage.setItem('selectedAliasRow', JSON.stringify(row));
    this.router.navigate(['/alias/detail', row.id, encodeURIComponent(row.name)]);
  }

  // Filtro: riparte sempre da pagina 0
  onFilterChanged(value: any) {
    this.searchQuery = value?.name ?? '';
    this.pageIndex = 0;
    this.loadAliasList(this.pageIndex, this.pageSize, 'name:asc', this.searchQuery);
  }

  // Ordinamento: riparte sempre da pagina 0
  onSortChanged(sort: Sort) {
    this.sortField = sort.active;
    this.sortDirection = sort.direction || 'asc';
    this.sortBy = `${this.sortField}:${this.sortDirection}`;
    this.pageIndex = 0;
    this.loadAliasList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
  }

  // Cambio pagina / page size
  onPageChanged(ev: PageEvent) {
    this.pageIndex = ev.pageIndex;
    this.pageSize  = ev.pageSize;
    this.loadAliasList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
  }

  openDialog(event: { data: any; type: 'add' | 'edit' | 'delete' }) {
    const dialogRef = this.dialog.open(ModalComponent, {
      width: '400px',
      data: {
        action: event.type,
        payload: event.data ?? { name: '' },
        visibleFields: ['name'],
        labels: { name: 'Nome*' },
        hiddenFields: ['type'],
        hiddenFieldsValues: this.hiddenFieldsValues,
        // attiva validator
        fieldValidators: {
          name: [
            Validators.pattern(/^(?!.*[-_]{2})[a-z0-9](?:[a-z0-9-_]*[a-z0-9])?$/)
          ]
        }
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      
      this.showFullScreenLoading = true;
      
      if (event.type === 'add') {
        this.aliasService.addNewAlias(result).pipe(finalize(() => { this.showFullScreenLoading = false; })).subscribe({
          next: () => {
            
            this.loadAliasList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
            this.aliasList$.pipe(take(1)).subscribe( () => {
              this.notificationService.entitySuccess('add', 'Alias');
            })
          },
          error: (err) => {
            console.log('errore nella richiesta', err);
            this.notificationService.entityError('add', 'Alias');
          } 
        });

      } else if(event.type === 'edit') {
        this.aliasService.updateAliasName(result).pipe(finalize(() => { this.showFullScreenLoading = false; })).subscribe({
          next: () => {
            this.loadAliasList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
            this.aliasList$.pipe(take(1)).subscribe( () => {
              this.notificationService.entitySuccess('update', 'Alias');
            })
          },
          error: (err) => {
            console.log('errore nella richiesta', err);
            this.notificationService.entityError('update', 'Alias');
          }
        }) 
      } else if (event.type === 'delete') {
        this.aliasService.deleteAlias(result.id).pipe(finalize(() => { this.showFullScreenLoading = false; })).subscribe({
          next: () => {
            if (this.pageIndex > 0 && this.dataSourceAlias.data.length === 1) {
              this.pageIndex = this.pageIndex - 1;
            }
            this.loadAliasList(this.pageIndex, this.pageSize, this.sortBy, this.searchQuery);
            this.aliasList$.pipe(take(1)).subscribe(()=> {
              this.notificationService.entitySuccess('delete', 'Alias');
            })
          },
          error: (err) => {
            if(err.status !== 409){
              console.log('errore nella richiesta', err);
              this.notificationService.entityError('delete', 'Alias');
            }  
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.aliasList$.complete();
  }
}